import noScroll from "@/utils/noScroll.js";

/**
 * Виджет всплывающего информационного окна (модуль popup).
 *
 * Окно показывается сразу после инициализации. Его содержимое разбито на
 * шаги (data-popup-step), из которых сервер рендерит только включённые:
 *  - country — выбор страны: «Казахстан» ведёт к следующему шагу (или
 *    закрывает окно, если условий нет), «Другие страны» — к шагу deny;
 *  - terms — основной контент с кнопками согласия и отказа;
 *  - deny — отказ в доступе со ссылкой на главную.
 * Если шага deny нет, туда, где он должен был показаться, выполняется
 * переход на главную (data-main-url).
 *
 * Крестик закрытия и кнопка согласия фиксируют cookie, имя которой передаётся
 * в data-cookie-name, — закрытое окно сервер больше не рендерит. Срок жизни
 * cookie — data-cookie-max-age в секундах, null — до конца сессии браузера.
 * Пустое имя cookie означает показ при каждом входе: закрытие не запоминается.
 * Отказ (другая страна, «не принимаю») cookie не ставит, чтобы окно снова
 * показалось, пока пользователь не подтвердил условия.
 *
 * data-blocking — блокирующий вариант (отображение на весь экран): корневой
 * элемент служит overlay и на время показа выключается прокрутка страницы.
 */
export default function infoPopup(element, data) {
    const cookieName = data.cookieName;
    const cookieMaxAge = data.cookieMaxAge;
    const isBlocking = data.blocking === true;
    const mainUrl = String(data.mainUrl || "/");
    const openClass = "open";
    // Длительность синхронизирована с transition корневого элемента в CSS.
    const hideDuration = 400;

    const popupWindow = element.querySelector(".popup-window");
    const steps = {};
    element.querySelectorAll("[data-popup-step]").forEach((node) => {
        steps[node.dataset.popupStep] = node;
    });

    let isOpen = false;

    function rememberDismissal() {
        if (!cookieName) {
            return;
        }
        // Без max-age cookie сессионная — живёт до конца сессии браузера.
        const maxAge = Number(cookieMaxAge) > 0 ? "; max-age=" + Number(cookieMaxAge) : "";
        document.cookie = encodeURIComponent(cookieName) + "=1; path=/" + maxAge + "; SameSite=Lax";
    }

    function showStep(name) {
        Object.entries(steps).forEach(([stepName, node]) => {
            node.hidden = stepName !== name;
        });
        // Новый шаг начинается с начала, даже если предыдущий был прокручен.
        if (popupWindow) {
            popupWindow.scrollTop = 0;
        }
    }

    function deny() {
        if (steps.deny) {
            showStep("deny");
        } else {
            window.location.href = mainUrl;
        }
    }

    function open() {
        if (isOpen) {
            return;
        }
        isOpen = true;
        // Класс добавляется в следующем кадре, чтобы сработал transition появления.
        requestAnimationFrame(() => element.classList.add(openClass));
        if (isBlocking) {
            noScroll.hideScroll();
        }
    }

    function dismiss() {
        if (!isOpen) {
            return;
        }
        isOpen = false;
        rememberDismissal();
        element.classList.remove(openClass);
        if (isBlocking) {
            noScroll.showScroll();
        }
        // Удаляем из DOM после завершения анимации скрытия.
        setTimeout(() => element.remove(), hideDuration);
    }

    // Крестик закрытия и кнопка согласия — закрывают окно и ставят cookie.
    element.querySelectorAll("[data-popup-dismiss]").forEach((node) => {
        node.addEventListener("click", dismiss);
    });

    // Кнопка отказа при наличии шага deny переключает на него; без шага deny
    // она остаётся обычной ссылкой на главную.
    element.querySelectorAll("[data-popup-deny]").forEach((node) => {
        node.addEventListener("click", (event) => {
            event.preventDefault();
            deny();
        });
    });

    const countrySelect = element.querySelector("[data-popup-country]");
    if (countrySelect) {
        // Браузер может восстановить выбор из прошлого посещения страницы
        // (bfcache, автозаполнение) — начинаем всегда с подсказки.
        countrySelect.selectedIndex = 0;
        countrySelect.addEventListener("change", () => {
            if (countrySelect.value !== "kz") {
                deny();
            } else if (steps.terms) {
                showStep("terms");
            } else {
                dismiss();
            }
        });
    }

    showStep(steps.country ? "country" : "terms");
    open();
}
